<template>
  <z-button
    link
    type="primary"
    :size="size"
    :class="ns.e('upload-cell-trigger')"
    @click.stop="openDialog"
  >
    {{ t('z.table.uploadCell.upload') }}
  </z-button>

  <z-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="560px"
      append-to-body
      align-center
      :class="ns.e('upload-dialog')"
      @closed="onDialogClosed"
    >
      <z-upload
        v-model:file-list="draftFileList"
        :action="uploadAction"
        :data="uploadData"
        :headers="uploadHeaders"
        :http-request="gridImageUploadRequest"
        list-type="picture-card"
        :limit="uploadLimit"
        :multiple="uploadMultiple"
        :accept="accept"
        @success="onUploadSuccess"
        @error="onUploadError"
      />

      <template #footer>
        <z-button @click="cancelDialog">
          {{ t('z.messagebox.cancel') }}
        </z-button>
        <z-button type="primary" @click="confirmDialog">
          {{ t('z.messagebox.confirm') }}
        </z-button>
      </template>
    </z-dialog>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import { useGlobalConfig } from '@zoryn/components/config-provider'
import ZButton from '@zoryn/components/button'
import ZDialog from '@zoryn/components/dialog'
import { ZMessage } from '@zoryn/components/message'
import ZUpload, { ajaxUpload } from '@zoryn/components/upload'
import { useLocale, useNamespace } from '@zoryn/hooks'
import { getAccessToken, getCommonResultMessage, isCommonResultSuccess } from '@zoryn/network'
import {
  createUploadFilesFromUrls,
  extractUploadFileUrls,
  resolveImageCellUrls,
  resolveUploadResponseUrl,
  serializeImageCellValue,
} from '../utils/image-cell'
import {
  applyUploadPathWithFilename,
  resolveColumnUploadAction,
  resolveColumnUploadData,
} from '../utils/resolve-column-upload'
import { beginRowEdit } from '../methods/edit'

import type { UploadFile, UploadRequestHandler, UploadUserFile } from '@zoryn/components/upload'
import type { ComponentSize } from '@zoryn/constants'
import type { DataGrid, GridColumnInfo } from '../types'

const props = defineProps<{
  row: Record<string, unknown>
  column: GridColumnInfo
  size?: ComponentSize
}>()

const emit = defineEmits<{
  'update:value': [value: unknown]
}>()

const ns = useNamespace('grid')
const { t } = useLocale()
const $grid = inject('$grid') as DataGrid
const gridGlobalConfig = useGlobalConfig('grid')

const dialogVisible = ref(false)
const draftFileList = ref<UploadUserFile[]>([])

const fieldValue = computed(() => props.row[props.column.field!])
const previewUrls = computed(() => resolveImageCellUrls(fieldValue.value))

const uploadAction = computed(() =>
  resolveColumnUploadAction(props.column, $grid.options, gridGlobalConfig.value),
)
const uploadData = computed(() => resolveColumnUploadData(props.column, props.row))
const uploadLimit = computed(() => props.column.uploadLimit ?? 9)
const uploadMultiple = computed(() => uploadLimit.value !== 1)
const accept = 'image/*'

const gridImageUploadRequest: UploadRequestHandler = (option) => {
  const data = applyUploadPathWithFilename(
    option.data ? { ...(option.data as Record<string, unknown>) } : undefined,
    option.file?.name,
  )
  return ajaxUpload({ ...option, data })
}

const uploadHeaders = computed(() => {
  if (props.column.uploadHeaders) {
    return props.column.uploadHeaders
  }
  const token = getAccessToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
})

const dialogTitle = computed(() =>
  t('z.table.uploadCell.imageTitle', {
    title: props.column.title ?? '',
  })
)

function openDialog() {
  if (!beginRowEdit($grid, props.row)) {
    return
  }

  if (!uploadAction.value) {
    ZMessage.warning(t('z.table.uploadCell.missingUploadAction'))
    return
  }

  draftFileList.value = createUploadFilesFromUrls(previewUrls.value)
  dialogVisible.value = true
}

function onUploadSuccess(response: unknown, uploadFile: UploadFile) {
  if (!isCommonResultSuccess(response)) {
    uploadFile.status = 'fail'
    ZMessage.error(getCommonResultMessage(response))
    return
  }

  const url = resolveUploadResponseUrl(response)
  if (url) {
    uploadFile.url = url
    return
  }

  uploadFile.status = 'fail'
  ZMessage.error(t('z.table.uploadCell.uploadFailed'))
}

function onUploadError(error: Error) {
  ZMessage.error(error?.message || t('z.table.uploadCell.uploadFailed'))
}

function cancelDialog() {
  dialogVisible.value = false
}

function confirmDialog() {
  if (draftFileList.value.some((file) => file.status === 'uploading')) {
    ZMessage.warning(t('z.table.uploadCell.uploading'))
    return
  }

  const pendingUpload = draftFileList.value.filter(
    (file) => file.raw && file.status !== 'success',
  )
  if (pendingUpload.length > 0) {
    ZMessage.warning(t('z.table.uploadCell.uploadFailed'))
    return
  }

  const urls = extractUploadFileUrls(draftFileList.value)
  if (draftFileList.value.length > 0 && urls.length === 0) {
    ZMessage.warning(t('z.table.uploadCell.uploadFailed'))
    return
  }

  emit('update:value', serializeImageCellValue(urls, fieldValue.value))
  dialogVisible.value = false
}

function onDialogClosed() {
  draftFileList.value = []
}
</script>
